/// <amd-module name="Release/Commands/CreateReleaseCommandService" />
import * as React from "react";

import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import { IVssService, VssService, Services } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { ReactRootComponent, IVssLayoutManager } from "VSS/Platform/Layout";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";

import { IMenuItem } from "VSSUI/Menu";

import { IReleaseDefinitionPivotCommandContext, IStartReleaseCommandContext } from "Release/Client/ExtensionContracts/IMenuCommandContracts";
import { IReleaseService } from "Release/Client/Service/Release";
import { ReleaseStartMetadata, Release } from "Release/Client/Contracts/Release";
import { createUrlForRoute } from "Release/Helpers/Link";
import { hasRmPermissions, ReleaseManagementSecurityPermissions } from "Release/Helpers/Permission";
import { publishClickEvent, publishSuccessEvent, publishFailedEvent } from "Release/Helpers/Telemetry";
import * as Resources from "Release/Commands/Resources";
import { IVssPerformanceService } from 'VSS/Platform/Performance';

export const createReleaseCommand: (context: IReleaseDefinitionPivotCommandContext, createReleaseCallback?: (release: Release) => void) => IMenuItem | undefined = (context: IReleaseDefinitionPivotCommandContext, createReleaseCallback?: (release: Release) => void) => {
    if (hasPermissions(context)) {
        return {
            id: "create-release-command",
            text: Resources.CreateRelease,
            iconProps: { iconName: "Rocket" },
            onActivate: () => {
                context.pageContext.getService<ICreateReleaseCommandService>("ICreateReleaseCommandService").createRelease(context, createReleaseCallback);
            },
        };
    }
};

export const createDratReleaseCommand: (context: IReleaseDefinitionPivotCommandContext) => IMenuItem | undefined = (context: IReleaseDefinitionPivotCommandContext) => {
    if (hasPermissions(context)) {
        return {
            id: "create-draft-release-command",
            text: Resources.CreateDraftRelease,
            iconProps: { iconName: "PageEdit" },
            onActivate: () => {
                context.pageContext.getService<ICreateReleaseCommandService>("ICreateReleaseCommandService").createDraft(context);
            }
        };
    }
};

export const startReleaseCommand: (context: IStartReleaseCommandContext) => IMenuItem | undefined = (context: IStartReleaseCommandContext) => {
    if (hasPermissions(context)) {
        return {
            id: "start-release-command",
            text: Resources.StartRelease,
            iconProps: { iconName: "MSNVideos" },
            onClick: () => {
                context.pageContext.getService<ICreateReleaseCommandService>("ICreateReleaseCommandService").startRelease(context);
            }
        };
    }
};


export interface ICreateReleaseCommandService extends IVssService {
    createRelease(context: IReleaseDefinitionPivotCommandContext, createReleaseCallback?: (release: Release) => void): void;
    createDraft(context: IReleaseDefinitionPivotCommandContext): void;
    startRelease(context: IStartReleaseCommandContext): void;
}

class CreateReleaseCommandService extends VssService implements ICreateReleaseCommandService {

    public createRelease(context: IReleaseDefinitionPivotCommandContext, createReleaseCallback?: (release: Release) => void): void {
        this._queueRelease(context, false, createReleaseCallback);
    }

    public createDraft(context: IReleaseDefinitionPivotCommandContext): void {
        let pipelineReleaseParameters = {
            isDraft: true,
            definitionId: context.pipelineId
        } as ReleaseStartMetadata;

        publishClickEvent(this.pageContext, createDraftReleaseFeature, context.telemetrySource);

        this.pageContext.getService<IReleaseService>("IReleaseService").createRelease(pipelineReleaseParameters)
            .then((release: Release) => {
                // Redirect to the Release editor
                const queryParams: { [key: string]: string } = {
                    "releaseId": release.id.toString(10),
                    "_a": "release-environments-editor"
                };

                publishSuccessEvent(this.pageContext, createDraftReleaseFeature, context.telemetrySource);
                const draftUrl = createUrlForRoute(this.pageContext, "ms.vss-releaseManagement-web.draft-release-route", queryParams);
                FastPageSwitch(this.pageContext, draftUrl, true);
            },
                (error) => {
                    // TODO : How do we handle errors for contributed menu items??
                    publishFailedEvent(this.pageContext, createDraftReleaseFeature, context.telemetrySource);
                });
    }

    public startRelease(context: IStartReleaseCommandContext): void {
        this._queueRelease(context, true);
    }

    private _queueRelease(context: IReleaseDefinitionPivotCommandContext | IStartReleaseCommandContext, isDraft: boolean, createReleaseCallback?: (release: Release) => void): void {
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const PerfConstants = { FeatureArea: "CD", CreateReleaseDialogScenario: "VSO.CD.OpenCreateReleaseDialog" };
        performanceService.startScenario(PerfConstants.CreateReleaseDialogScenario, true);
        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            publishClickEvent(this.pageContext, startReleaseFeature, context.telemetrySource);
            const props = {
                definitionId: context.pipelineId,
                startReleaseMode: isDraft,
                releaseId: isDraft ? (context as IStartReleaseCommandContext).releaseId : undefined,
                onQueueRelease: (release: Release) => {
                    publishSuccessEvent(this.pageContext, startReleaseFeature, context.telemetrySource);
                    const href = createUrlForRoute(this.pageContext, "ms.vss-releaseManagement-web.cd-release-progress-default-route", {
                        "_a": "release-pipeline-progress",
                        "releaseId": release.id.toString(10)
                    });
                    const successMessage = React.createElement(
                        FormatComponent,
                        { format: Resources.ReleaseQueuedMessage },
                        React.createElement(FPSLink, {
                            rel: "noopener noreferrer", href: href, telemetrySource: (isDraft ? "start-release-command-success" : "create-release-command-success"), className: "messagebar-rel-link"
                        }, release.name)
                    );

                    if (createReleaseCallback) {
                        createReleaseCallback(release);
                    }

                    if (isDraft) {
                        context.successCallback(successMessage, false, release);
                    }
                    else {
                        context.successCallback(successMessage);
                    }

                    onDismiss();
                },
                endPerfScenario: () => performanceService.endScenario(PerfConstants.FeatureArea, PerfConstants.CreateReleaseDialogScenario, true),
                onClose: () => onDismiss()
            };

            return React.createElement(ReactRootComponent,
                {
                    pageContext: this.pageContext
                },
                React.createElement(LegacyComponent, {
                    modules: ["PipelineWorkflow/Scripts/SharedComponents/CreateRelease/CreateReleasePanelHelper"],
                    dependentContributionIds: ["ms.vss-releaseManagement-web.release-service-data"],
                    loadingComponent: () => React.createElement(Spinner, { className: "flex flex-grow absolute-fill", size: SpinnerSize.large }),
                    wrappedType: "createReleasePanelProxy",
                    wrappedProps: props
                }));
        });
    }
}

function hasPermissions(context: IReleaseDefinitionPivotCommandContext): boolean {
    return hasRmPermissions(context.pageContext, context.folderPath, context.pipelineId, ReleaseManagementSecurityPermissions.CreateReleases);
}

const createReleaseFeature: string = "Command_CreateRelease";
const createDraftReleaseFeature: string = "Command_CreateDraftRelease";
const startReleaseFeature: string = "Command_StartRelease";

Services.add("ICreateReleaseCommandService", { serviceFactory: CreateReleaseCommandService });
